import React, { useEffect, useState } from "react";
import { X, Clock3, User, Loader2, History } from "lucide-react";
import NotificationAlert from "../../../../CustomAlerts/NotificationAlert";
import { GetRecruitmentHistory } from "../Services/RecruitmentService";

const RecruitmentHistoryDrawer = ({ open, onClose, recruitmentUID }) => {
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [alert, setAlert] = useState(null);

  useEffect(() => {
    if (open && recruitmentUID) {
      fetchHistory();
    }
  }, [open, recruitmentUID]);

  const fetchHistory = async () => {
    try {
      setLoading(true);
      setAlert(null);

      const res = await GetRecruitmentHistory(recruitmentUID);

      if (res?.status === "success") {
        setHistory(res?.historyDetails || []);
      } else if (res?.message === "No history found.") {
        setHistory([]);
      } else {
        setAlert({
          type: "error",
          message: res?.message || "Something went wrong.",
        });
      }
    } catch (error) {
      setAlert({
        type: "error",
        message:
          error?.response?.data?.message ||
          error?.message ||
          "Server error while fetching recruitment history.",
      });
    } finally {
      setLoading(false);
    }
  };

  const getChangedFields = (current, previous) => {
    const ignoreFields = [
      "actionDate",
      "lastUpdatedAt",
      "actionBy",
      "actionType",
      "assignedTo",
      "assignedBy",
    ];

    // FIRST ENTRY
    if (!previous) {
      return [
        {
          field: "Lead Created",
          oldValue: "",
          newValue: "Lead created successfully",
        },
      ];
    }

    const orderedFields = [
      "targetOrganization",
      "targetRole",
      "callResponse",
      "currentJobLocation",
      "preferredJobLocation",
      "qualification",
      "totalExperience",
      "currentCompany",
      "designation",
      "skill",
      "appliedFor",
      "currentCTC",
      "expectedCTC",
      "reasonOfLeaving",
      "department",
      "noticePeriod",
    ];

    const changes = [];

    orderedFields.forEach((key) => {
      if (ignoreFields.includes(key)) return;

      const currentValue = current?.[key]?.toString()?.trim();
      const previousValue = previous?.[key]?.toString()?.trim();

      // IGNORE EMPTY VALUES
      if (!currentValue) return;

      // ONLY CHANGED VALUES
      if (currentValue !== previousValue) {
        changes.push({
          field: key,
          oldValue: previousValue || "",
          newValue: currentValue,
        });
      }
    });

    return changes;
  };

  const formatFieldName = (field) => {
    return field
      .replace(/([A-Z])/g, " $1")
      .replace(/^./, (str) => str.toUpperCase());
  };

  return (
    <>
      <div
        className={`fixed inset-0 z-[999] transition-all duration-300 ${
          open ? "visible" : "invisible"
        }`}
      >
        {/* OVERLAY */}
        <div
          onClick={onClose}
          className={`absolute inset-0 bg-black/30 backdrop-blur-[2px] transition-opacity duration-300 ${
            open ? "opacity-100" : "opacity-0"
          }`}
        />

        {/* DRAWER */}
        <div
          className={`absolute top-0 right-0 h-full w-full max-w-[900px] bg-[#f4f7fb] shadow-2xl flex flex-col transform transition-transform duration-300 ${
            open ? "translate-x-0" : "translate-x-full"
          }`}
        >
          {/* HEADER */}
          <div className="bg-white border-b border-slate-200 px-6 py-5 flex items-start justify-between">
            <div className="flex items-start gap-4">
              <div className="h-12 w-12 rounded-2xl bg-blue-50 border border-blue-100 flex items-center justify-center flex-shrink-0">
                <History size={22} className="text-blue-600" />
              </div>

              <div>
                <h2 className="text-[20px] font-semibold text-slate-800 leading-none">
                  Recruitment History
                </h2>

                <p className="text-[15px] text-slate-500 mt-2">
                  Track all recruitment activity and updates
                </p>
              </div>
            </div>

            <button
              onClick={onClose}
              className="h-10 w-10 rounded-xl hover:bg-slate-100 flex items-center justify-center transition-all"
            >
              <X size={22} className="text-slate-500" />
            </button>
          </div>

          {/* BODY */}
          <div className="flex-1 overflow-y-auto bg-[#f6f8fc] px-8 py-8">
            {loading ? (
              <div className="h-full flex items-center justify-center">
                <Loader2 size={38} className="animate-spin text-blue-600" />
              </div>
            ) : history?.length === 0 ? (
              <div className="h-full flex items-center justify-center text-slate-500">
                No recruitment history found.
              </div>
            ) : (
              <div className="space-y-5">
                {history.map((item, index) => {
                  const previous = history[index + 1];

                  const isEmptyHistory = (obj) => {
                    if (!obj) return false;

                    const fieldsToCheck = [
                      "currentJobLocation",
                      "preferredJobLocation",
                      "qualification",
                      "totalExperience",
                      "currentCompany",
                      "designation",
                      "skill",
                      "appliedFor",
                      "currentCTC",
                      "expectedCTC",
                      "reasonOfLeaving",
                      "department",
                      "noticePeriod",
                      "targetOrganization",
                      "targetRole",
                      "callResponse",
                    ];

                    return fieldsToCheck.every(
                      (field) =>
                        !obj[field] ||
                        obj[field] === "" ||
                        obj[field] === null ||
                        obj[field] === undefined,
                    );
                  };

                  let changes = [];

                  // LEAD CREATED ENTRY
                  if (previous && isEmptyHistory(previous)) {
                    const orderedFields = [
                      "targetOrganization",
                      "targetRole",
                      "callResponse",
                      "currentJobLocation",
                      "preferredJobLocation",
                      "qualification",
                      "totalExperience",
                      "currentCompany",
                      "designation",
                      "skill",
                      "appliedFor",
                      "currentCTC",
                      "expectedCTC",
                      "reasonOfLeaving",
                      "department",
                      "noticePeriod",
                    ];

                    changes = orderedFields
                      .filter(
                        (key) =>
                          item?.[key] &&
                          item?.[key] !== "" &&
                          item?.[key] !== null,
                      )
                      .map((key) => ({
                        field: key,
                        oldValue: "",
                        newValue: item[key],
                      }));

                    // ADD LEAD CREATED ON TOP
                    changes.unshift({
                      field: "Lead Created",
                      oldValue: "",
                      newValue: "Lead created successfully",
                    });
                  } else {
                    changes = getChangedFields(item, previous);
                  }

                  // SKIP PURE EMPTY ENTRY
                  if (isEmptyHistory(item)) return null;

                  if (changes.length === 0) return null;

                  return (
                    <div key={index} className="relative flex gap-4">
                      {/* LEFT TIMELINE */}
                      <div className="relative flex flex-col items-center">
                        {index !== 0 && (
                          <div className="absolute top-0 h-8 w-[2px] bg-blue-200" />
                        )}

                        <div className="relative z-10">
                          <div className="absolute inset-0 rounded-2xl bg-blue-400/20 blur-xl" />

                          <div className="relative h-11 w-11 rounded-2xl bg-white border border-blue-100 shadow-md flex items-center justify-center">
                            <History size={18} className="text-blue-600" />
                          </div>
                        </div>

                        {index !== history.length - 1 && (
                          <div className="flex-1 w-[2px] min-h-[35px] bg-gradient-to-b from-blue-400 to-blue-100" />
                        )}
                      </div>

                      {/* CARD */}
                      <div className="group relative flex-1 rounded-[22px] overflow-hidden border border-slate-100 bg-white shadow-sm transition-all duration-300">
                        {/* HEADER */}
                        <div className="px-5 py-3 border-b border-slate-100/80 bg-gradient-to-br from-white via-white to-blue-50/40">
                          <div className="flex items-center justify-between gap-4">
                            <div className="min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <h3 className="text-[15px] font-semibold text-slate-800">
                                  {changes.some(
                                    (x) => x.field === "Lead Created",
                                  )
                                    ? "Lead Created Successfully"
                                    : changes.length > 1
                                      ? "Multiple Fields Updated"
                                      : `${formatFieldName(
                                          changes[0]?.field,
                                        )} Updated`}
                                </h3>

                                <span className="px-2.5 py-[5px] rounded-full text-[11px] font-semibold bg-blue-50 text-blue-600 border border-blue-100 leading-none">
                                  Activity
                                </span>
                              </div>

                              <div className="flex items-center justify-between gap-4 mt-2 flex-wrap">
                                {/* LEFT SIDE */}
                                <div className="flex items-center gap-4 text-[12px] text-slate-500 flex-wrap">
                                  <div className="flex items-center gap-1.5">
                                    <User size={13} />

                                    <span className="font-medium">
                                      {item?.actionBy}
                                    </span>
                                  </div>

                                  <div className="flex items-center gap-1.5">
                                    <Clock3 size={13} />
                                    {item?.actionDate
                                      ? item.actionDate.slice(0, 16)
                                      : "-"}
                                  </div>
                                </div>

                                {/* RIGHT SIDE CALL RESPONSE */}
                                {!changes.some(
                                  (x) => x.field === "Lead Created",
                                ) &&
                                  item?.callResponse && (
                                    <div
                                      className={`px-3 py-1 rounded-full text-[11px] font-semibold border ${
                                        item?.callResponse?.toUpperCase() ===
                                        "CONNECTED"
                                          ? "bg-green-50 text-green-600 border-green-100"
                                          : item?.callResponse?.toUpperCase() ===
                                              "NOT CONNECTED"
                                            ? "bg-red-50 text-red-600 border-red-100"
                                            : "bg-blue-50 text-blue-600 border-blue-100"
                                      }`}
                                    >
                                      {item?.callResponse}
                                    </div>
                                  )}
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* CHANGES */}
                        {!changes.some((x) => x.field === "Lead Created") && (
                          <div className="p-3 space-y-2">
                            {/* TARGET DETAILS */}
                            {!changes.some((x) => x.field === "Lead Created") &&
                              (item?.targetRole ||
                                item?.targetOrganization) && (
                                <div className="flex flex-wrap gap-3 mb-3">
                                  {item?.targetRole && (
                                    <div className="px-4 py-2 rounded-xl bg-blue-50 border border-blue-100 text-[13px]">
                                      <span className="font-semibold text-slate-700">
                                        Target Role :
                                      </span>{" "}
                                      <span className="text-blue-600 font-semibold">
                                        {item?.targetRole}
                                      </span>
                                    </div>
                                  )}

                                  {item?.targetOrganization && (
                                    <div className="px-4 py-2 rounded-xl bg-blue-50 border border-blue-100 text-[13px]">
                                      <span className="font-semibold text-slate-700">
                                        Target Organization :
                                      </span>{" "}
                                      <span className="text-blue-600 font-semibold">
                                        {item?.targetOrganization}
                                      </span>
                                    </div>
                                  )}
                                </div>
                              )}

                            {changes
                              .filter(
                                (change) =>
                                  change.field !== "Lead Created" &&
                                  change.field !== "targetRole" &&
                                  change.field !== "targetOrganization" &&
                                  change.field !== "callResponse",
                              )
                              .map((change, idx) => (
                                <div
                                  key={idx}
                                  className="group/item relative overflow-hidden rounded-[18px] border border-slate-100 bg-white px-4 py-2 shadow-sm hover:shadow-md hover:border-blue-100 hover:bg-blue-50/[0.15] transition-all duration-300"
                                >
                                  <div className="flex flex-wrap items-center gap-2 text-[14px] leading-relaxed">
                                    <span className="font-semibold text-slate-800">
                                      {formatFieldName(change.field)}
                                    </span>

                                    {change.field === "Lead Created" ? (
                                      <span className="px-3 py-1.5 rounded-2xl bg-gradient-to-br from-green-50 to-emerald-50 border border-emerald-100 text-emerald-600 font-semibold shadow-sm">
                                        {change.newValue}
                                      </span>
                                    ) : !change.oldValue ||
                                      change.oldValue === "-" ? (
                                      <>
                                        <span className="text-slate-400">
                                          added as
                                        </span>

                                        <span className="px-3 py-1.5 rounded-2xl bg-gradient-to-br from-[#e6f6fd] to-[#d6effc] border border-[#b8e3fa] text-[#009FE3] font-semibold shadow-sm">
                                          {change.newValue || "-"}
                                        </span>
                                      </>
                                    ) : (
                                      <>
                                        <span className="text-slate-400">
                                          changed from
                                        </span>

                                        <span className="px-3 py-1.5 rounded-2xl bg-gradient-to-br from-slate-50 to-blue-50 border border-blue-100 text-[#004687] font-semibold shadow-sm">
                                          {change.oldValue}
                                        </span>

                                        <span className="text-slate-400 font-medium">
                                          →
                                        </span>

                                        <span className="px-3 py-1.5 rounded-2xl bg-gradient-to-br from-[#e6f6fd] to-[#d6effc] border border-[#b8e3fa] text-[#009FE3] font-semibold shadow-sm">
                                          {change.newValue || "-"}
                                        </span>
                                      </>
                                    )}
                                  </div>
                                </div>
                              ))}
                          </div>
                        )}
                        {/* FOOTER */}
                        <div className="px-5 py-2 border-t border-slate-100/80 bg-gradient-to-r from-slate-50/80 to-white flex items-center justify-between">
                          <p className="text-[12px] italic text-slate-500">
                            {item?.actionType}
                          </p>

                          <div className="px-3 py-1 rounded-full bg-slate-100 text-slate-500 text-[12px] font-medium">
                            Audit Log
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* FOOTER */}
          <div className="bg-white border-t border-slate-200 px-4 py-2 flex justify-end">
            <button
              onClick={onClose}
              className="px-6 py-2.5 rounded-2xl border border-slate-300 bg-white hover:bg-slate-50 text-[14px] font-medium transition"
            >
              Close
            </button>
          </div>
        </div>
      </div>

      {/* ALERT */}
      {alert && (
        <NotificationAlert
          show={!!alert}
          type={alert?.type}
          message={alert?.message}
          onClose={() => setAlert(null)}
        />
      )}
    </>
  );
};

export default RecruitmentHistoryDrawer;
